/**
 * Automatic Sync (connectors.enabled) — merchant control over Borraq-initiated
 * provider work for ONE connector (marketplace account).
 *
 * STATUS-TRUTH-002 source trace (repository evidence only, no hosted access):
 *  - Automatic price and inventory work is gated twice: the cron selector asks
 *    only for connectors.enabled = true, and begin_catalogue_domain_sync /
 *    assert_catalogue_domain_sync_claim require enabled before any provider
 *    write. Direct Edge Function invocation cannot skip that claim.
 *  - Listing publication is gated at request time and again at claim time;
 *    both provider publishers additionally require an actively claimed job.
 *  - Order import and fulfilment sync do NOT consult enabled. Turning the
 *    switch off does not stop them.
 *  - Inbound marketplace webhooks are refused while the connector is disabled.
 *  - A request already handed to the marketplace cannot be recalled, and a
 *    write already completed is never undone.
 *
 * The copy below must never describe this setting as a circuit breaker and
 * must never claim it stops every provider operation.
 */

export const AUTOMATIC_SYNC_STALE_WARNING =
  "Automatic sync is off. Stock and price changes will not be sent to this marketplace until you turn it back on; changes already queued will wait.";

/**
 * Accurate description of what the existing setting controls, in ordinary
 * merchant language. New automatic work stops; queued stock/price work waits;
 * order import and fulfilment continue; in-flight work is not recalled.
 */
export const AUTOMATIC_SYNC_SCOPE_NOTE =
  "Controls automatic catalogue work for this store: publishing approved listings, and sending stock, price and listing changes to this marketplace for listings that are already live. Turning it off stops new automatic work. Stock and price changes that are already queued wait and go out when you turn it back on, but a publication request that has not started is not carried out. Order import and fulfilment sync keep running because they follow the connection, not this switch. A request that has already reached the marketplace cannot be recalled, and marketplace event notifications are not processed while it is off.";

/**
 * How `enabled` relates to each provider operation.
 *
 *  A — confirmed gated by connectors.enabled before provider I/O.
 *  B — gated only at a particular stage or write boundary.
 *  C — not gated by connectors.enabled.
 *  D — cannot be established from available repository source.
 *
 * Every entry cites the file that proves it, so the merchant copy can be
 * re-checked instead of trusted. This matrix is documentation and test data
 * only: it never renders a control and never changes behaviour.
 */
export type AutomaticSyncScopeClass = "A" | "B" | "C" | "D";

export interface AutomaticSyncScopeEvidence {
  file: string;
  detail: string;
}

export interface AutomaticSyncScopeEntry {
  key: string;
  operation: string;
  provider: "ebay" | "bigcommerce" | "all";
  classification: AutomaticSyncScopeClass;
  merchantSummary: string;
  evidence: ReadonlyArray<AutomaticSyncScopeEvidence>;
}

export const AUTOMATIC_SYNC_CONTROL_SCOPE: ReadonlyArray<AutomaticSyncScopeEntry> = [
  {
    key: "price_sync",
    operation: "Automatic price synchronisation",
    provider: "all",
    classification: "A",
    merchantSummary: "Stopped. Queued price work waits and resumes when switched on.",
    evidence: [
      {
        file: "supabase/functions/listing-domain-sync/index.ts",
        detail: "process-price-jobs selects price_publish jobs only where connectors.enabled = true, before LIMIT.",
      },
      {
        file: "supabase/migrations/0214_authoritative_catalogue_domain_claims.sql",
        detail: "begin_catalogue_domain_sync raises before claim when the connector is not enabled.",
      },
    ],
  },
  {
    key: "inventory_sync",
    operation: "Automatic inventory / available-quantity synchronisation",
    provider: "all",
    classification: "A",
    merchantSummary: "Stopped. Queued stock work waits and resumes when switched on.",
    evidence: [
      {
        file: "supabase/functions/listing-domain-sync/index.ts",
        detail: "process-inventory-jobs selects inventory_publish jobs only where connectors.enabled = true.",
      },
      {
        file: "supabase/functions/listing-available-quantity-sync/index.ts",
        detail: "Inventory writes run under a durable claim asserted through assert_catalogue_domain_sync_claim.",
      },
      {
        file: "supabase/migrations/0214_authoritative_catalogue_domain_claims.sql",
        detail: "begin_catalogue_domain_sync and assert_catalogue_domain_sync_claim both require enabled.",
      },
    ],
  },
  {
    key: "listing_publication",
    operation: "Catalogue publishing of approved listings",
    provider: "all",
    classification: "A",
    merchantSummary: "Not carried out while off; the publication request must be made again.",
    evidence: [
      {
        file: "supabase/migrations/0206_authoritative_listing_publication_lifecycle.sql",
        detail: "request_listing_publication and claim_listing_publication_jobs both require enabled plus authorised auth_status.",
      },
      {
        file: "supabase/functions/ebay-catalogue-publish/index.ts",
        detail: "LIVE refuses without an actively claimed listing_publish job.",
      },
      {
        file: "supabase/functions/bigcommerce-inventory-sync/index.ts",
        detail: "LIVE refuses without a claimed running listing_publish job for the same listing and connector.",
      },
    ],
  },
  {
    key: "domain_sync_mid_job",
    operation: "Catalogue domain work already claimed",
    provider: "all",
    classification: "B",
    merchantSummary: "Each remaining domain write re-checks the switch; writes already completed stay applied.",
    evidence: [
      {
        file: "supabase/migrations/0214_authoritative_catalogue_domain_claims.sql",
        detail: "assert_catalogue_domain_sync_claim re-checks enabled, listing status and authorisation before each domain write.",
      },
      {
        file: "supabase/functions/listing-domain-sync/index.ts",
        detail: "Every domain asserts the claim immediately before provider I/O, and a failed finalisation leaves a running claim for reconciliation.",
      },
    ],
  },
  {
    key: "order_import",
    operation: "Order import (marketplace polling)",
    provider: "all",
    classification: "C",
    merchantSummary: "Continues. Import follows the connection, not the Automatic Sync switch.",
    evidence: [
      {
        file: "supabase/functions/marketplace-order-ingest/index.ts",
        detail: "A connector is treated as live when auth_status is connected/authorized/configured OR enabled is true; enabled is never required.",
      },
      {
        file: "supabase/functions/marketplace-sync/index.ts",
        detail: "process-jobs runs order_import and fulfilment_sync without branching on enabled; only catalogue domain job types are delegated to the claim-gated worker.",
      },
    ],
  },
  {
    key: "ebay_fulfilment_sync",
    operation: "eBay fulfilment synchronisation",
    provider: "ebay",
    classification: "C",
    merchantSummary: "Continues. Dispatch and tracking go to eBay regardless of the switch.",
    evidence: [
      {
        file: "supabase/functions/ebay-fulfilment-sync/index.ts",
        detail: "The connector lookup reads id, org_id, auth_status, marketplace_site and name; enabled is never read.",
      },
    ],
  },
  {
    key: "bigcommerce_fulfilment_sync",
    operation: "BigCommerce fulfilment synchronisation",
    provider: "bigcommerce",
    classification: "C",
    merchantSummary: "Continues. Shipments are posted to BigCommerce regardless of the switch.",
    evidence: [
      {
        file: "supabase/functions/bigcommerce-fulfilment-sync/index.ts",
        detail: "Enabled is passed into the connection guard, which does not use it.",
      },
      {
        file: "supabase/functions/_shared/bigcommerce-fulfilment.ts",
        detail: "isAuthorizedBigCommerceConnector explicitly voids opts.enabled and judges only credentials plus auth_status.",
      },
    ],
  },
  {
    key: "inbound_webhooks",
    operation: "Inbound marketplace event webhooks",
    provider: "all",
    classification: "A",
    merchantSummary: "Refused while off. The marketplace may resend events later.",
    evidence: [
      {
        file: "supabase/functions/ebay-webhook-handler/index.ts",
        detail: "Returns 409 Connector is disabled when enabled is false.",
      },
      {
        file: "supabase/functions/bigcommerce-webhook-handler/index.ts",
        detail: "Returns 409 Connector is disabled when enabled is false.",
      },
      {
        file: "supabase/functions/marketplace-webhook/index.ts",
        detail: "Returns 409 Connector is disabled when enabled is false.",
      },
    ],
  },
  {
    key: "in_flight_request",
    operation: "A provider request already in flight",
    provider: "all",
    classification: "C",
    merchantSummary: "Not recalled. A request already sent to the marketplace cannot be cancelled.",
    evidence: [
      {
        file: "supabase/functions/listing-domain-sync/index.ts",
        detail: "Provider I/O is issued inside the request; no code path revokes an HTTP request already sent.",
      },
    ],
  },
  {
    key: "retry_of_failed_work",
    operation: "Retry of confirmed failed work",
    provider: "all",
    classification: "B",
    merchantSummary: "Can be requeued, but the claim refuses it while the switch is off.",
    evidence: [
      {
        file: "supabase/migrations/0214_authoritative_catalogue_domain_claims.sql",
        detail: "retry_sync_job refuses running or remote_review work; a requeued job still has to pass the claim, which requires enabled.",
      },
    ],
  },
  {
    key: "hosted_scheduling",
    operation: "Whether hosted cron jobs are actually scheduled",
    provider: "all",
    classification: "D",
    merchantSummary: "Not verifiable from repository source.",
    evidence: [
      {
        file: "supabase/migrations/0174_price_publish_cron_gateway_auth.sql",
        detail: "The price clock depends on pg_cron plus vault secrets configured in the hosted project.",
      },
      {
        file: "supabase/migrations/0180_inventory_publish_cron.sql",
        detail: "The inventory clock depends on pg_cron plus vault secrets configured in the hosted project.",
      },
    ],
  },
];

/** Merchant copy that must stay true for a given automated operation. */
export function automaticSyncScopeFor(key: string): AutomaticSyncScopeEntry | null {
  return AUTOMATIC_SYNC_CONTROL_SCOPE.find((entry) => entry.key === key) ?? null;
}

/** Operations confirmed to sit outside the switch, for wording regression tests. */
export function automaticSyncOperationsOutsideControl(): string[] {
  return AUTOMATIC_SYNC_CONTROL_SCOPE
    .filter((entry) => entry.classification === "C")
    .map((entry) => entry.operation);
}

/**
 * Accepts the app Supabase client's `.rpc`. public.connectors is browser-
 * readable but NOT browser-writable, so Automatic Sync can only be persisted
 * through the narrow owner/admin RPC `set_connector_automatic_sync`.
 */
export type SetConnectorAutomaticSyncClient = {
  rpc: (
    name: string,
    params: Record<string, unknown>,
  ) => PromiseLike<{
    data: { success?: boolean; enabled?: boolean; error?: string } | null;
    error: { message?: string } | null;
  }>;
};

export function formatAutomaticSyncError(raw: unknown, turningOn: boolean): string {
  const detail =
    typeof raw === "string" && raw.trim()
      ? raw.trim()
      : raw && typeof raw === "object" && "message" in raw && typeof (raw as { message: unknown }).message === "string"
        ? String((raw as { message: string }).message).trim()
        : "Please try again.";
  // Never surface tokens/secrets if a gateway echoes them.
  const safe = detail
    .replace(/Bearer\s+\S+/gi, "[redacted]")
    .replace(/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[redacted]");
  const verb = turningOn ? "on" : "off";
  return `Unable to turn Automatic Sync ${verb}: ${safe}`;
}

export function shouldWarnAutomaticSyncOff(opts: {
  enabled: boolean;
  hasLiveListings: boolean;
}): boolean {
  return opts.enabled === false && opts.hasLiveListings === true;
}

/** connector_ids that currently have at least one provider-confirmed live listing. */
export function connectorIdsWithLiveListings(
  rows: ReadonlyArray<{ connector_id: string | null | undefined }>,
): Set<string> {
  const out = new Set<string>();
  for (const row of rows) {
    const id = String(row.connector_id ?? "").trim();
    if (id) out.add(id);
  }
  return out;
}

/**
 * Persist connectors.enabled for one org-scoped connector via the narrow
 * owner/admin RPC. Only `enabled` is sent — never credential or auth_status.
 */
export async function setConnectorAutomaticSync(
  client: SetConnectorAutomaticSyncClient,
  opts: { orgId: string; connectorId: string; enabled: boolean },
): Promise<{ ok: true; enabled: boolean } | { ok: false; error: string }> {
  const orgId = String(opts.orgId ?? "").trim();
  const connectorId = String(opts.connectorId ?? "").trim();
  if (!orgId || !connectorId) {
    return { ok: false, error: formatAutomaticSyncError("Missing connector or organisation.", opts.enabled) };
  }

  const { data, error } = await client.rpc("set_connector_automatic_sync", {
    p_org_id: orgId,
    p_connector_id: connectorId,
    p_enabled: opts.enabled,
  });

  if (error) {
    return { ok: false, error: formatAutomaticSyncError(error.message ?? error, opts.enabled) };
  }
  if (!data || data.success !== true) {
    return {
      ok: false,
      error: formatAutomaticSyncError(
        data?.error ??
          "Update was not applied. You may not have permission, or the connector was not found.",
        opts.enabled,
      ),
    };
  }
  if (data.enabled !== opts.enabled) {
    return {
      ok: false,
      error: formatAutomaticSyncError("Connector state did not change as requested.", opts.enabled),
    };
  }
  return { ok: true, enabled: opts.enabled };
}
